import React from 'react';
import { FlightTrailEvent, RunSplitEvent, UnitSystem } from '../types/event';
import { formatAltitude, formatSpeed, heartRateToSignalLevel, stressClassFor } from '../utils/formatters';

interface StressBarsProps {
  filledCount: number;
}

const StressBars: React.FC<StressBarsProps> = ({ filledCount }) => (
  <span className="flight-trail-bars" aria-hidden="true">
    {Array.from({ length: 5 }).map((_, idx) => (
      <span
        key={idx}
        className={`flight-trail-bar b${idx + 1} ${idx < filledCount ? 'filled' : 'empty'}`}
      />
    ))}
  </span>
);

const stressToFilledCount = (stress: number): number => Math.min(Math.max(1, Math.ceil(stress)), 5);

export type MarkerGroup = 'Climbing' | 'Sinking' | 'Turning';
export const ALL_MARKER_GROUPS: MarkerGroup[] = ['Climbing', 'Sinking', 'Turning'];

const MARKER_GROUP_GLYPH: Record<MarkerGroup, string> = {
  Climbing: '▲',
  Sinking: '▼',
  Turning: '↺',
};

interface TrailDrawerProps {
  eventType: string;
  isOpen: boolean;
  flightTrailEvents: FlightTrailEvent[];
  runSplits: RunSplitEvent[];
  unitSystem: UnitSystem;
  visibleMarkerGroups: Set<MarkerGroup>;
  onMarkerGroupToggle: (group: MarkerGroup) => void;
  onToggleAllMarkers: () => void;
}

const TrailDrawer: React.FC<TrailDrawerProps> = ({
  eventType,
  isOpen,
  flightTrailEvents,
  runSplits,
  unitSystem,
  visibleMarkerGroups,
  onMarkerGroupToggle,
  onToggleAllMarkers,
}) => {
  const hasContent = eventType === 'flight' || eventType === 'run';
  if (!hasContent) return null;

  const allMarkersVisible = visibleMarkerGroups.size === ALL_MARKER_GROUPS.length;

  return (
    <div className={`side-drawer side-drawer-left${isOpen ? '' : ' drawer-closed'}`}>
      <div className="drawer-inner">
        {eventType === 'flight' && (
          <>
            <div className="flight-trail-header">
              <div className="flight-trail-header-top">
                <div>
                  <div className="flight-trail-title">Event Trail</div>
                  <div className="flight-trail-subtitle">Action · Stress (1–5)</div>
                </div>
                <button
                  className={`marker-toggle-btn${allMarkersVisible ? ' all-on' : ' all-off'}`}
                  onClick={onToggleAllMarkers}
                  aria-label={allMarkersVisible ? 'Hide all map markers' : 'Show all map markers'}
                  title={allMarkersVisible ? 'Hide all map markers' : 'Show all map markers'}
                >
                  👁 {allMarkersVisible ? 'Markers' : 'Off'}
                </button>
              </div>
              <div className="marker-filter-row">
                {ALL_MARKER_GROUPS.map((group) => (
                  <button
                    key={group}
                    className={`marker-filter-chip${visibleMarkerGroups.has(group) ? ' active' : ' inactive'}`}
                    onClick={() => onMarkerGroupToggle(group)}
                    aria-pressed={visibleMarkerGroups.has(group)}
                    title={`${visibleMarkerGroups.has(group) ? 'Hide' : 'Show'} ${group.toLowerCase()} markers`}
                  >
                    {MARKER_GROUP_GLYPH[group]} {group}
                  </button>
                ))}
              </div>
            </div>
            <div className="flight-trail-list">
              {flightTrailEvents.length === 0 ? (
                <div className="flight-trail-empty">Waiting for flight events…</div>
              ) : (
                [...flightTrailEvents].reverse().map((e) => (
                  <div key={e.id} className="flight-trail-row">
                    <div className="flight-trail-time">
                      {new Date(e.timestamp).toLocaleString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </div>
                    <div className="flight-trail-action">
                      {e.action}
                      <span className="flight-trail-detail">
                        {typeof e.approxAltitudeM === 'number'
                          ? ` · ~${formatAltitude(e.approxAltitudeM, unitSystem)}`
                          : ''}
                        {typeof e.approxSpeedKmh === 'number'
                          ? ` · ~${formatSpeed(e.approxSpeedKmh, unitSystem)}`
                          : ''}
                        {e.detail ? ` · ${e.detail}` : ''}
                      </span>
                    </div>
                    <div
                      className={`flight-trail-stress ${stressClassFor(e.stress)}`}
                      aria-label={`Stress level ${e.stress} out of 5`}
                      title={`Stress level ${e.stress} / 5`}
                    >
                      <span className="flight-trail-stress-label">Stress:</span>
                      <StressBars filledCount={stressToFilledCount(e.stress)} />
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {eventType === 'run' && (
          <>
            <div className="run-splits-header">
              <div>
                <div className="run-splits-title">Km splits</div>
                <div className="run-splits-subtitle">Km · Heart rate</div>
              </div>
            </div>
            <div className="run-splits-list">
              {runSplits.length === 0 ? (
                <div className="run-splits-empty">Waiting for 1 km splits…</div>
              ) : (
                [...runSplits].reverse().map((s) => {
                  const avg = s.avgHeartRateBpm;
                  const filledCount = avg !== null ? heartRateToSignalLevel(avg) : 0;
                  return (
                    <div key={s.id} className="run-splits-row">
                      <div className="run-splits-time">
                        {new Date(s.timestamp).toLocaleString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        })}
                      </div>
                      <div className="run-splits-km">{s.km}k</div>
                      <div className="run-splits-hr">
                        {avg === null ? (
                          '—'
                        ) : (
                          <div
                            className={`flight-trail-stress ${stressClassFor(filledCount)}`}
                            aria-label={`Average heart rate ${avg} bpm`}
                            title={`Heart rate: ${avg} bpm`}
                          >
                            <span className="flight-trail-stress-label">HR:</span>
                            <StressBars filledCount={filledCount} />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TrailDrawer;
